<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0">Configuración</p>
              <argon-button
                color="success"
                size="sm"
                class="ms-auto"
                @click="save"
                :disabled="isSaving"
              >
                Guardar
              </argon-button>
            </div>
          </div>
          <div class="card-body">
            <p class="text-uppercase text-sm">Información de la empresa</p>
            <div class="row">
              <div class="col-md-12">
                <label class="form-control-label">Nombre de la empresa</label>
                <argon-input type="text" v-model="form.company_name" placeholder="Ej: Mi Empresa" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">NIT</label>
                <argon-input type="text" v-model="form.nit" placeholder="Número de identificación tributaria" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">Descripción</label>
                <textarea
                  class="form-control"
                  v-model="form.description"
                  rows="3"
                  placeholder="Descripción o notas"
                ></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Correo electrónico</label>
                <argon-input type="email" v-model="form.email" placeholder="contacto@empresa.com" />
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Teléfono</label>
                <argon-input type="text" v-model="form.phone" placeholder="+57 300 123 4567" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">Dirección</label>
                <argon-input type="text" v-model="form.address" placeholder="Dirección física" />
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Beneficios de tareas</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.benefits_of_tasks"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Chat AI</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.chat_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.chat_enabled" class="mt-3">
                  <label class="form-control-label">API Key de Gemini</label>
                  <argon-input
                    type="password"
                    v-model="form.gemini_api_key"
                    :placeholder="hasGeminiApiKey ? '•••••••••••••••• (dejar vacío para mantener)' : 'AIza...'"
                  />
                  <p class="text-xs text-muted mt-2 mb-0">
                    Obtén tu key en
                    <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer">
                      Google AI Studio
                    </a>.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Shopify</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.shopify_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.shopify_enabled" class="mt-3">
                  <label class="form-control-label">Dominio de la tienda</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_store"
                    placeholder="mi-tienda.myshopify.com"
                  />
                  <p class="text-xs text-muted mt-2 mb-0">
                    POST
                    <code>https://{{ shopifyStoreSlug || 'nombre-de-tu-tienda' }}.myshopify.com/admin/oauth/access_token</code>
                    con <code>grant_type=client_credentials</code>
                  </p>

                  <label class="form-control-label mt-3">Client ID</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_client_id"
                    placeholder="Client ID de tu app Shopify"
                  />

                  <label class="form-control-label mt-3">Client Secret</label>
                  <argon-input
                    type="password"
                    v-model="form.shopify_client_secret"
                    :placeholder="hasShopifyClientSecret ? '•••••••••••••••• (dejar vacío para mantener)' : 'Client Secret'"
                  />

                  <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                    <argon-button
                      color="info"
                      size="sm"
                      :disabled="isGeneratingToken"
                      @click="generateShopifyToken"
                    >
                      <span v-if="isGeneratingToken">Generando...</span>
                      <span v-else>Generar token</span>
                    </argon-button>
                    <argon-button
                      color="secondary"
                      size="sm"
                      :disabled="isLoadingPaymentModes"
                      @click="viewShopifyPaymentModes"
                    >
                      <span v-if="isLoadingPaymentModes">Consultando...</span>
                      <span v-else>Ver modos de pago</span>
                    </argon-button>
                    <span
                      class="text-xs"
                      :class="hasShopifyToken ? 'text-success' : 'text-muted'"
                    >
                      {{ hasShopifyToken ? 'Token generado y guardado' : 'Aún no hay token generado' }}
                    </span>
                  </div>
                  <p class="text-xs text-muted mt-2 mb-0">
                    POST
                    <code>https://{{ shopifyStoreSlug || 'nombre-de-tu-tienda' }}.myshopify.com/payments_apps/api/2026-04/graphql.json</code>
                  </p>

                  <p class="text-xs text-muted mt-2 mb-0">
                    Guarda las credenciales y pulsa <strong>Generar token</strong>.
                    Se enviará <code>application/x-www-form-urlencoded</code> con Client ID y Client Secret.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Logos (opcional)</p>
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-control-label">Ícono / Logo claro</label>
                    <input
                      type="file"
                      class="form-control"
                      accept="image/*"
                      @change="onLogoLightChange"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-control-label">Ícono / Logo oscuro</label>
                    <input
                      type="file"
                      class="form-control"
                      accept="image/*"
                      @change="onLogoDarkChange"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";

export default {
  name: "Settings",
  components: {
    ArgonInput,
    ArgonButton,
  },
  data() {
    return {
      isSaving: false,
      isGeneratingToken: false,
      isLoadingPaymentModes: false,
      form: {
        company_name: "",
        nit: "",
        description: "",
        email: "",
        phone: "",
        address: "",
        benefits_of_tasks: false,
        chat_enabled: false,
        gemini_api_key: "",
        shopify_enabled: false,
        shopify_store: "",
        shopify_client_id: "",
        shopify_client_secret: "",
      },
      hasGeminiApiKey: false,
      hasShopifyToken: false,
      hasShopifyClientSecret: false,
      logoLightFile: null,
      logoDarkFile: null,
    };
  },
  mounted() {
    this.loadSettings();
  },
  computed: {
    shopifyStoreSlug() {
      const store = (this.form.shopify_store || "").trim().toLowerCase();
      if (!store) return "";
      return store.replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.myshopify\.com$/, "");
    },
  },
  methods: {
    async loadSettings() {
      try {
        const response = await this.$store.dispatch("settings/get");
        const data = response.data;
        this.form = {
          company_name: data.company_name || "",
          nit: data.nit || "",
          description: data.description || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          benefits_of_tasks: !!data.benefits_of_tasks,
          chat_enabled: !!data.chat_enabled,
          gemini_api_key: "",
          shopify_enabled: !!data.shopify_enabled,
          shopify_store: data.shopify_store || "",
          shopify_client_id: data.shopify_client_id || "",
          shopify_client_secret: "",
        };
        this.hasGeminiApiKey = !!data.has_gemini_api_key;
        this.hasShopifyToken = !!data.has_shopify_token;
        this.hasShopifyClientSecret = !!data.has_shopify_client_secret;
      } catch (error) {
        console.error("Error al cargar la configuración:", error);
        await Swal.fire("Error", "Error al cargar la configuración", "error");
      }
    },
    onLogoLightChange(event) {
      this.logoLightFile = event.target.files?.[0] || null;
    },
    onLogoDarkChange(event) {
      this.logoDarkFile = event.target.files?.[0] || null;
    },
    async save() {
      if (this.form.chat_enabled && !this.form.gemini_api_key.trim() && !this.hasGeminiApiKey) {
        await Swal.fire(
          "Error",
          "Ingresa la API key de Gemini para activar Chat AI.",
          "error"
        );
        return;
      }

      if (this.form.shopify_enabled && !this.form.shopify_store.trim()) {
        await Swal.fire(
          "Error",
          "Ingresa el dominio de la tienda Shopify.",
          "error"
        );
        return;
      }

      if (this.form.shopify_enabled && !this.form.shopify_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify.", "error");
        return;
      }

      if (
        this.form.shopify_enabled &&
        !this.form.shopify_client_secret.trim() &&
        !this.hasShopifyClientSecret
      ) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify.", "error");
        return;
      }

      this.isSaving = true;
      try {
        const formData = new FormData();
        formData.append("company_name", this.form.company_name);
        formData.append("nit", this.form.nit ?? "");
        formData.append("description", this.form.description);
        formData.append("email", this.form.email);
        formData.append("phone", this.form.phone);
        formData.append("address", this.form.address);
        formData.append("benefits_of_tasks", this.form.benefits_of_tasks ? "1" : "0");
        formData.append("chat_enabled", this.form.chat_enabled ? "1" : "");
        if (this.form.gemini_api_key.trim()) {
          formData.append("gemini_api_key", this.form.gemini_api_key.trim());
        }
        formData.append("shopify_enabled", this.form.shopify_enabled ? "1" : "");
        formData.append("shopify_store", this.form.shopify_store.trim());
        formData.append("shopify_client_id", this.form.shopify_client_id.trim());
        if (this.form.shopify_client_secret.trim()) {
          formData.append("shopify_client_secret", this.form.shopify_client_secret.trim());
        }
        if (this.logoLightFile) {
          formData.append("logo_path", this.logoLightFile);
        }
        if (this.logoDarkFile) {
          formData.append("logo_path_dark", this.logoDarkFile);
        }

        await this.$store.dispatch("settings/save", formData);
        this.$store.commit("settings/SET_CHAT_ENABLED", this.form.chat_enabled);
        this.$store.commit("settings/SET_SHOPIFY_ENABLED", this.form.shopify_enabled);
        this.hasGeminiApiKey = this.hasGeminiApiKey || !!this.form.gemini_api_key.trim();
        this.form.gemini_api_key = "";
        this.hasShopifyClientSecret =
          this.hasShopifyClientSecret || !!this.form.shopify_client_secret.trim();
        this.form.shopify_client_secret = "";
        await Swal.fire("Éxito", "Configuración guardada correctamente", "success");
        this.logoLightFile = null;
        this.logoDarkFile = null;
        document.querySelectorAll('input[type="file"]').forEach((el) => (el.value = ""));
      } catch (error) {
        console.error("Error al guardar:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "Error al guardar la configuración",
          "error"
        );
      } finally {
        this.isSaving = false;
      }
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    formatPaymentModesHtml(endpoint, paymentModes) {
      const endpointHtml = `<p class="text-xs text-muted mb-2"><code>${this.escapeHtml(endpoint)}</code></p>`;

      if (!Array.isArray(paymentModes) || paymentModes.length === 0) {
        return `${endpointHtml}<p class="text-sm mb-0">No se encontraron modos de pago en Shopify.</p>`;
      }

      const rows = paymentModes
        .map((mode) => {
          const code = this.escapeHtml(mode.code || "—");
          const name = this.escapeHtml(
            mode.name && mode.name !== mode.code ? mode.name : "—"
          );
          const source = this.escapeHtml(mode.source || "—");

          return `<tr><td><code>${code}</code></td><td>${name}</td><td class="text-xs">${source}</td></tr>`;
        })
        .join("");

      return `${endpointHtml}
        <div style="max-height: 360px; overflow: auto; text-align: left;">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th>Código Shopify</th>
                <th>Nombre</th>
                <th>Fuente</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    },
    async viewShopifyPaymentModes() {
      if (!this.form.shopify_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify.", "error");
        return;
      }

      if (!this.hasShopifyToken) {
        await Swal.fire(
          "Error",
          "Genera el token de Shopify antes de consultar los modos de pago.",
          "error"
        );
        return;
      }

      this.isLoadingPaymentModes = true;

      try {
        const response = await shopifyService.getPaymentModes();

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudieron consultar los modos de pago.");
        }

        await Swal.fire({
          title: "Modos de pago Shopify",
          html: this.formatPaymentModesHtml(
            response.data.endpoint,
            response.data.payment_modes
          ),
          width: 720,
        });
      } catch (error) {
        console.error("Error al consultar modos de pago Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al consultar modos de pago Shopify.",
          "error"
        );
      } finally {
        this.isLoadingPaymentModes = false;
      }
    },
    async generateShopifyToken() {
      if (!this.form.shopify_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify.", "error");
        return;
      }
      if (!this.form.shopify_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify.", "error");
        return;
      }
      if (!this.form.shopify_client_secret.trim() && !this.hasShopifyClientSecret) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify.", "error");
        return;
      }

      this.isGeneratingToken = true;
      try {
        const formData = new FormData();
        formData.append("shopify_enabled", "1");
        formData.append("shopify_store", this.form.shopify_store.trim());
        formData.append("shopify_client_id", this.form.shopify_client_id.trim());
        if (this.form.shopify_client_secret.trim()) {
          formData.append("shopify_client_secret", this.form.shopify_client_secret.trim());
        }
        await this.$store.dispatch("settings/save", formData);
        this.hasShopifyClientSecret =
          this.hasShopifyClientSecret || !!this.form.shopify_client_secret.trim();

        const payload = {
          client_id: this.form.shopify_client_id.trim(),
        };

        if (this.form.shopify_client_secret.trim()) {
          payload.client_secret = this.form.shopify_client_secret.trim();
        }

        const response = await shopifyService.generateToken(payload);

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudo generar el token.");
        }

        this.hasShopifyToken = true;
        this.form.shopify_client_secret = "";
        await Swal.fire("Shopify", response.data.message || "Token generado correctamente.", "success");
      } catch (error) {
        console.error("Error al generar token Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al generar token Shopify.",
          "error"
        );
      } finally {
        this.isGeneratingToken = false;
      }
    },
  },
};
</script>
